export const successColor = '#7CC522FF';
export const dangerColor = '#ff4d4d';

export const primaryColor = '#0A6EFF';
export const secondaryColor = '#ee8b4f';

export const newSlideColor = '#4caf50';
export const newSlideBackground = 'rgba(76,175,80,0.2)';

export const newVersionColor = '#ff9800';
export const newVersionBackground = 'rgba(255,152,0,0.15)';

export const duplicateSlideColor = '#9e9e9e';
export const duplicateSlideBackground = 'rgba(158,158,158,0.15)';

export const updatedSlideBorderColor = '#2196f3';
export const updatedSlideColor = '#1976d2';
export const updatedSlideBackground = '#bbdefb';

export const textColor = '#181A30';

export const defaultBorderColor = '#d2d2d2';

export const defaultBorderRadius = '4px';

const defaultTheme = {
  fontFamily: {
    main: 'Montserrat, -apple-system, BlinkMacSystemFont, sans-serif',
    code: 'Monaco, monospace',
  },
  fontSize: {
    h1: '2.57rem',
    h2: '2rem',
    h3: '18px',
    h4: '1rem', // 16px
    h5: '0.92rem', // ~15px
    h6: '0.75rem', // 12px
    lg: '1.2rem', // 19px
    default: '0.875rem', // 14px
    sm: '0.75rem', // 12px
    xs: '0.6875rem', // 11px
    body: '0.8125rem', // 13px
    badge: '0.625rem', // 10px
    sectionHeader: '1rem', // 16px
    buttonLarge: '1.5 rem', // 24px
    button: '0.8125rem', // 13px
    input: '0.8125rem', // 13px
  },
  spacing: {
    xSmall: '0.625rem', // 10px
    small: '0.5rem',
    default: '0.8rem',
    medium: '1.25rem',
    large: '1.9rem',
    xLarge: '3rem',
  },
  radius: {
    xSmall: '0.30rem',
    small: '0.57rem',
    normal: '1rem',
    default: '1rem',
    card: '4px',
    chip: '0.625rem', // 10px
    medium: '0.5rem', // 8px
  },
  shadows: {
    sm: '0 2px 8px rgba(0,0,0,0.06)',
    md: '0 4px 16px rgba(0,0,0,0.08)',
    lg: '0 8px 24px rgba(0,0,0,0.12)',
    xl: '0 8px 32px rgba(0,0,0,0.14)',
    chip: '0 4px 12px rgba(37,99,235,0.15)',
    card: `0 1px 6px rgba(0, 0, 0, 0.07)`,
    cardLg: `0 2px 12px rgba(0, 0, 0, 0.1)`,
  },
  size: {
    medium: '2.5rem',
    large: '104px',
    small: '24px',
    iconStrokeWidth: '1.25',
  },
  link: {
    color: '#454b49',
  },
  defaultBorderRadius: defaultBorderRadius,
  pageHeader: {
    height: '60px',
    color: '#454b49',
    backgroundColor: '#fff',
    borderBottom: '1px solid #333',
    logo: {
      height: '28px',
      brandHeight: '22px',
    },
    branding: {
      text: {
        fontSize: '24px',
        color: '#454b49',
      },
    },
    separator: {
      color: '#000',
      height: '32px',
    },
    nav: {
      color: '#454b49',
      hoverColor: '#257789',
    },
  },
  sectionHeader: {
    color: '#454b49',
  },
  padding: {
    small: '1rem 0.5rem',
  },
  palette: {
    // legacy styles - left only for some shared components
    white: '#ffffff',
    textColor: textColor,
    primaryColor: primaryColor,
    secondaryColor: secondaryColor,
    pdfIconColor: 'rgb(213,80,80)',
    folderIconColor: 'rgb(101, 101, 101)',
    defaultIconColor: 'rgb(91, 127, 245)',
    pptxIconColor: '#D04423',
    error: '#fe615a',
    errorDimmed: '#ffb3b0',
    dangerDimmed: '#fef2f2',
    grey: '#ececec',
    darkGrey: '#8d8d8d',
    linkHoverColor: '#0A6EFF',
    linkColor: '#454b49',
    // textLinkColor: '#0A6EFF', // gb(10, 110, 255)
    textLinkColor: '#0A6EFF',
    aiHoverBackground: 'rgba(124, 58, 237, 0.06)',
    aiBackground: '#7c3aed',
    warning: '#f4c136',
    danger: dangerColor,
    success: successColor,
    // hoverBackgroundColor: '#25778917',
    hoverBackgroundColor: 'rgba(227, 227, 227, 0.87)',
    selectedBackgroundColor: 'lightskyblue',
    buttonTextColor: '#262626',
    // new styles
    gray: '#4B5563',
    grayLighter: '#9799A6',
    black: 'black',
    text: textColor,
    textDimmed: '#6B7280', //'#757575',
    border: '#A4D6EC',
    inputBorder: '#E5E7EB',
    inputBackground: '#f9fafb',
    inputPlaceholder: '#9ca3af',
    stroke: '#E8ECEF',
    accent: '#A5F3FF',
    accentText: '#181A30',
    placeholderText: '#72748A',
    background: '#E5FAFF',
    iconButtonHoverBackground: '#f2f2f4',
    overlayBackground: '#1f2937',
    overlayBorder: '#374151',
    overlayText: '#d1d5db',
    overlayTextMuted: '#4B5563',
    overlayAccent: '#93c5fd',
    overlayItemActive: 'rgba(255,255,255,0.12)',
    overlayItemHover: '#374151',
    // card / new home design tokens
    cardBackground: '#ffffff',
    cardBorder: '#E5E7EB',
    cardBorderDarker: '#D1D5DB',
    cardBorderHover: '#cbd5e1',
    cardSurface: '#f1f5f9',
    textStrong: '#111827',
    textMedium: '#374151',
    textSubtle: '#718096',
    textMuted: '#4B5563',
    teal: '#4DB8CC',
    primaryDark: '#1E40AF',
    sectionHeader: '#454b49',
    linkBackground: '#EFF6FF',
    linkBackgroundHover: '#F0F6FF',
    primaryButton: '#0A6EFF',
    primaryButtonHover: '#0047B3',
    overBorder: '#0A6EFF',
    itemTypeColors: {
      workspace: '#D97706',
      folder: '#6B7280',
      presentation: '#0891b2',
      collection: '#0891b2',
      asset: '#059669',
      resource: '#d97706',
      repository: '#0891b2',
      'slide-repository': '#0891b2', // slidespace
      slide: '#7c3aed',
      template: '#0A6EFF',
    },
  },
  slides: {
    statusBadge: {
      new: {
        color: newSlideColor,
        background: newSlideBackground,
      },
      newVersion: {
        color: newVersionColor,
        background: newVersionBackground,
      },
      duplicate: {
        color: duplicateSlideColor,
        background: duplicateSlideBackground,
      },
      updated: {
        color: updatedSlideColor,
        background: updatedSlideBackground,
        borderColor: updatedSlideBorderColor,
      },
    },
  },
  groups: {
    libraryGroupBackgroundColor: 'rgb(238, 247, 249)',
    sortGroupBackgroundColor: 'rgb(218, 246, 252)',
    libraryGroupBorder: '2px solid rgb(65, 171, 193)',
    sortGroupBorder: '2px solid white',
    sortListGroupBorder: '2px solid rgb(65, 171, 193)',
  },
  slideStatusInfo: {
    newBackgroundColor: '#7cc522',
    newTextColor: '#ffffff',
    updatedBackgroundColor: '#0080ff',
    updatedTextColor: '#ffffff',
  },
  sortTabSideTabsContent: {
    rowGap: '5px',
    padding: '10px 20px 10px 20px',
    height: '580px',
  },
  slideSortThumbnail: {
    fontSize: '12px',
    color: 'rgba(33, 94, 255, 0.8)',
    backgroundColor: '#fff',
    hoverShadow: 'rgb(0 0 0 / 14%) 4px 8px 20px 0px',
    addedBackgroundColor: 'rgba(124, 197, 34, 0.2)',
    slideOverBarColor: 'blue',
    slideOverBarWidth: '2px',
    activeBorder: '4px solid rgb(95,143,192)',
    deleteButton: {
      backgroundColor: '#fff',
      color: '#8d8d8d',
      hoverColor: '#ff4d4d',
      disabledColor: '#ececec',
    },
  },
  slideDividerThumbnail: {
    backgroundColor: '#fff',
    color: 'rgba(33, 94, 255, 0.8)',
    fontSize: '14px',
    border: '3px dashed rgba(151, 151, 151, 0.59)',
    borderRadius: '4px',
    draggingOpacity: '0.7',
  },
  slideListItem: {
    fontSize: '12px',
    indexFontSize: '12px',
    fontWeight: 'bold',
    padding: '10px 10px',
    slideOverBarColor: 'blue',
    slideOverBarWidth: '2px',
    draggingOpacity: '0.9',
    backgroundColor: 'rgb(238,247,249)',
    containerBackgroundColor: 'white',
    containerPadding: '5px',
    addedBackgroundColor: 'rgb(237, 252, 219)',
    activeBorder: '3px solid rgb(95,143,192)',
  },
  zoomControls: {
    color: '#8d8d8d',
    backgroundColor: '#8f8f8f',
    buttonColor: '#8d8d8d',
    buttonDisabledColor: '#bfbfbf',
    sliderHoverShadow: '0px 0px 0px 8px rgb(0 0 0 / 10%);',
    sliderActiveShadow: '0px 0px 0px 14px rgb(0 0 0 / 16%);',
  },
  viewControls: {
    color: '#bfbfbf',
    activeColor: '#636363',
  },
  landingPage: {
    headerColor: '#353535',
    textColor: '#636363',
    pageBox: {
      backgroundColor: '#fff',
      maxWidth: '1080px',
    },
  },
  // next gen
  avatar: {
    backgroundColor: '#F1F2F4',
    textColor: '#181A30',
  },
  layout: {
    pageMaxWidth: '1440px',
  },
  dialog: {
    maxWidth: '900px',
    borderRadius: '6px',
    headerPadding: '16px 24px',
    bodyPadding: '24px',
    footerPadding: '12px 24px',
    overlayBackground: 'rgba(0, 0, 0, 0.5)',
  },
  PresentationPreviewDialog: {
    background: '#0f172a',
    buttonBackground: 'rgba(255, 255, 255, 0.08)',
    buttonBorder: 'rgba(255, 255, 255, 0.12)',
    buttonHoverBackground: 'rgba(255, 255, 255, 0.16)',
    scrollbarThumb: 'rgba(255, 255, 255, 0.2)',
    mutedText: 'rgba(255, 255, 255, 0.3)',
    badgeBackground: 'rgba(209, 213, 219, 0.15)',
    badgeBorder: 'rgba(209, 213, 219, 0.25)',
    badgeText: '#d1d5db',
    cardShadow: '0 24px 64px rgba(0, 0, 0, 0.5)',
    activeThumbnailRing: '0 0 0 2px rgba(37, 99, 235, 0.35)',
  },
};

// NOTE: update on the go...
// default theme should be replaced by light theme when we will be done with legacy components...
const coleyTheme = {
  ...defaultTheme,
  fontFamily: {
    main: 'Montserrat, -apple-system, BlinkMacSystemFont, sans-serif',
    code: 'Monaco, monospace',
  },
  fontSize: {
    h1: '26px',
    h2: '2rem',
    h3: '18px',
    h4: '1rem', // 16px
    h5: '0.92rem', // ~15px
    h6: '0.75rem', // 12px
    lg: '1.2rem', // 19px
    default: '0.875rem', // 14px
    sm: '0.75rem', // 12px
    xs: '0.6875rem', // 11px
    body: '0.8125rem', // 13px
    badge: '0.625rem', // 10px
    sectionHeader: '1rem', // 16px
  },
  spacing: {
    xSmall: '0.25rem', // 4px
    small: '0.5rem', // 8px
    default: '1rem', // 16px
    medium: '1.5rem', // 24px
    large: '2rem', // 32px
    xLarge: '3rem', // 48px
  },
  radius: {
    button: '8px',
    popupMenu: '8px',
    xSmall: '0.30rem',
    small: '0.57rem',
    normal: '1rem',
    default: '1rem',
    large: '1.9rem',
    xLarge: '3rem',
    card: '4px',
    chip: '0.625rem',
    medium: '0.5rem',
    input: '7px',
  },
  size: {
    medium: '2.5rem',
    large: '104px', // @todo
    small: '24px', // @todo
  },
};

const darkTheme = {
  ...coleyTheme,
  palette: {
    ...coleyTheme.palette,
    white: '#323C47',
    gray: '#b5b9d7',
    text: '#d9d9d9',
    background: '#363b3d',
    placeholderText: '#a5a7bd',
    iconStroke: '#9799a8',
    textStrong: '#f1f5f9',
    textMuted: '#94a3b8',
  },
};

export const themes = {
  default: defaultTheme,
  dark: darkTheme,
  coley: coleyTheme,
};
